import {
  Button,
  DatePicker,
  ExpandableSection,
  FormField,
  Input,
  SegmentedControl,
  SpaceBetween,
  TimeInput,
} from '@amzn/awsui-components-react';
import { ShiftInput, ShiftTimingType } from '@amzn/red-velvet-api';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OptionalLabel } from './Common';
import {
  toISODateString,
  toLocalTimeString,
  buildDateTimeFromParts,
  isCompleteTime,
} from '../../../../utils/dateFormatting';

const SHIFT_NAME_MAX_CHAR = 30;
const MAX_VOLUNTEERS_LIMIT = 2147483647; // Max signed 32-bit integer

export interface ShiftDetailsComponentProps {
  shift: ShiftInput;
  index: number;
  updateShift: (index: number, shift: ShiftInput) => void;
  onRemove?: (index: number) => void;
  errors?: Record<string, string>;
  expandOverride?: boolean;
  expandToggleKey?: number;
}

export function ShiftDetailsComponent({
  shift,
  index,
  updateShift,
  onRemove,
  errors,
  expandOverride,
  expandToggleKey,
}: ShiftDetailsComponentProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);

  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (expandOverride !== undefined) {
      setExpanded(expandOverride);
    }
  }, [expandToggleKey, expandOverride]);

  const shiftType = shift.shiftTimingType ?? 'scheduled';
  const [name, setName] = useState(shift.name ?? '');
  const [date, setDate] = useState(toISODateString(shift.startDateTime));
  const [startTime, setStartTime] = useState(toLocalTimeString(shift.startDateTime));
  const [endTime, setEndTime] = useState(toLocalTimeString(shift.endDateTime));
  const [startDate, setStartDate] = useState(toISODateString(shift.startDateTime));
  const [endDate, setEndDate] = useState(() => {
    const tomorrow = new Date(shift.startDateTime);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return toISODateString(shift.endDateTime) || toISODateString(tomorrow);
  });
  const [maxVolunteers, setMaxVolunteers] = useState(
    shift.maxSignup != null && shift.maxSignup > 0 ? String(shift.maxSignup) : ''
  );
  const [endDateError, setEndDateError] = useState('');
  const [endTimeError, setEndTimeError] = useState('');

  const shiftRef = useRef(shift);
  shiftRef.current = shift;

  const updateParent = (overrides: Partial<ShiftInput>) => {
    updateShift(index, { ...shiftRef.current, ...overrides });
  };

  /** Pad partial times like "00:1" → "00:01" to match Cloudscape auto-fill. */
  const padTime = (t: string): string => {
    const [h, m] = t.split(':');
    return h && m != null ? `${h.padStart(2, '0')}:${m.padStart(2, '0')}` : t;
  };

  const setShiftType = (type: string) => {
    const now = new Date();
    if (type === 'flexible') {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const sd = toISODateString(now);
      const ed = toISODateString(tomorrow);
      setStartDate(sd);
      setEndDate(ed);
      setStartTime('');
      setEndTime('');
      updateParent({
        shiftTimingType: type as ShiftTimingType,
        startDateTime: new Date(`${sd}T00:00`),
        endDateTime: new Date(`${ed}T23:59`),
      });
    } else {
      const today = toISODateString(now);
      setDate(today);
      setStartTime('');
      setEndTime('');
      const midnight = new Date(`${today}T00:00`);
      updateParent({
        shiftTimingType: type as ShiftTimingType,
        startDateTime: midnight,
        endDateTime: midnight,
      });
    }
  };

  const isScheduled = shiftType === 'scheduled';

  const headerDescription = isScheduled
    ? `${date || t('shiftStep.noDateSet')} • ${startTime && endTime ? `${startTime} - ${endTime}` : t('shiftStep.noTimeSet')}`
    : `${startDate || t('shiftStep.noDateSet')} - ${endDate || t('shiftStep.noDateSet')}`;

  return (
    <ExpandableSection
      expanded={expanded}
      onChange={({ detail }) => setExpanded(detail.expanded)}
      headerDescription={headerDescription}
      headerText={
        name ||
        (index === 0
          ? t('shiftStep.defaultShiftName')
          : t('shiftStep.shiftNumber', { number: index + 1 }))
      }
      variant="container"
      headerActions={
        onRemove ? (
          <Button
            variant="icon"
            iconName="close"
            ariaLabel={t('shiftStep.removeShift')}
            onClick={(e) => {
              e.stopPropagation();
              onRemove(index);
            }}
          />
        ) : undefined
      }
    >
      <SpaceBetween size="l">
        <FormField
          description={t('shiftStep.shiftNameDescription')}
          label={<OptionalLabel>{t('shiftStep.shiftName')}</OptionalLabel>}
          errorText={errors?.[`shift_${index}_name`]}
        >
          <Input
            placeholder={t('shiftStep.defaultShiftName')}
            value={name}
            onChange={({ detail }) => {
              if (detail.value.length > SHIFT_NAME_MAX_CHAR) return;
              setName(detail.value);
            }}
            onBlur={() => updateParent({ name })}
          />
        </FormField>

        <hr data-volunteer-center-hr />

        <FormField>
          <SegmentedControl
            label={t('shiftStep.shiftType')}
            options={[
              { iconName: 'calendar', id: 'scheduled', text: t('shiftStep.scheduled') },
              { iconName: 'calendar', id: 'flexible', text: t('shiftStep.flexible') },
            ]}
            selectedId={shiftType}
            onChange={({ detail }) => setShiftType(detail.selectedId)}
          />
        </FormField>

        {isScheduled ? (
          <>
            <FormField
              description={t('shiftStep.shiftDateDescription')}
              label={t('shiftStep.shiftDate')}
              errorText={errors?.[`shift_${index}_date`]}
            >
              <DatePicker
                placeholder="YYYY/MM/DD"
                value={date}
                onChange={({ detail }) => setDate(detail.value)}
                onBlur={() => {
                  const start = buildDateTimeFromParts(date, startTime);
                  const end = buildDateTimeFromParts(date, endTime);
                  updateParent({
                    ...(start && { startDateTime: start }),
                    ...(end && { endDateTime: end }),
                  });
                }}
                i18nStrings={{
                  previousMonthAriaLabel: t('shiftStep.previousMonthAriaLabel'),
                  nextMonthAriaLabel: t('shiftStep.nextMonthAriaLabel'),
                  todayAriaLabel: t('shiftStep.todayAriaLabel'),
                  openCalendarAriaLabel: (selectedDate) =>
                    selectedDate
                      ? t('shiftStep.chooseDateAriaLabel', { selectedDate })
                      : t('shiftStep.chooseDateDefaultAriaLabel'),
                }}
              />
            </FormField>
            <SpaceBetween direction="horizontal" size="l">
              <FormField
                description={t('shiftStep.timeZoneLabel')}
                label={t('shiftStep.shiftStartTime')}
                errorText={errors?.[`shift_${index}_startTime`]}
              >
                <TimeInput
                  format="hh:mm"
                  placeholder="hh:mm"
                  use24Hour={true}
                  value={startTime}
                  onChange={({ detail }) => {
                    setStartTime(detail.value);
                    if (
                      isCompleteTime(detail.value) &&
                      isCompleteTime(endTime) &&
                      endTime <= detail.value
                    ) {
                      setEndTimeError(t('shiftStep.endTimeMustBeAfterStartTime'));
                    } else {
                      setEndTimeError('');
                    }
                  }}
                  onBlur={() => {
                    const t = padTime(startTime);
                    if (isCompleteTime(t)) {
                      const start = buildDateTimeFromParts(date, t);
                      if (start) updateParent({ startDateTime: start });
                    }
                  }}
                />
              </FormField>
              <FormField
                description={t('shiftStep.timeZoneLabel')}
                label={t('shiftStep.shiftEndTime')}
                errorText={endTimeError || errors?.[`shift_${index}_endTime`]}
              >
                <TimeInput
                  format="hh:mm"
                  placeholder="hh:mm"
                  use24Hour={true}
                  value={endTime}
                  onChange={({ detail }) => {
                    setEndTime(detail.value);
                    if (
                      isCompleteTime(startTime) &&
                      isCompleteTime(detail.value) &&
                      detail.value <= startTime
                    ) {
                      setEndTimeError(t('shiftStep.endTimeMustBeAfterStartTime'));
                    } else {
                      setEndTimeError('');
                    }
                  }}
                  onBlur={() => {
                    const t = padTime(endTime);
                    if (isCompleteTime(t)) {
                      const end = buildDateTimeFromParts(date, t);
                      if (end) updateParent({ endDateTime: end });
                    }
                  }}
                />
              </FormField>
            </SpaceBetween>
          </>
        ) : (
          <SpaceBetween direction="horizontal" size="l">
            <FormField
              label={t('shiftStep.startDate')}
              description={t('shiftStep.startDateDescription')}
              errorText={errors?.[`shift_${index}_startDate`]}
            >
              <DatePicker
                placeholder="YYYY/MM/DD"
                value={startDate}
                onChange={({ detail }) => {
                  setStartDate(detail.value);
                  if (endDate && detail.value && endDate <= detail.value) {
                    setEndDateError(t('shiftStep.endDateMustBeAfterStartDate'));
                  } else {
                    setEndDateError('');
                  }
                }}
                onBlur={() => {
                  if (startDate) {
                    updateParent({ startDateTime: new Date(`${startDate}T00:00`) });
                  }
                }}
                i18nStrings={{
                  previousMonthAriaLabel: t('shiftStep.previousMonthAriaLabel'),
                  nextMonthAriaLabel: t('shiftStep.nextMonthAriaLabel'),
                  todayAriaLabel: t('shiftStep.todayAriaLabel'),
                  openCalendarAriaLabel: (selectedDate) =>
                    selectedDate
                      ? t('shiftStep.chooseStartDateAriaLabel', { selectedDate })
                      : t('shiftStep.chooseStartDateDefaultAriaLabel'),
                }}
              />
            </FormField>
            <FormField
              label={t('shiftStep.endDate')}
              description={t('shiftStep.endDateDescription')}
              errorText={endDateError || errors?.[`shift_${index}_endDate`]}
            >
              <DatePicker
                placeholder="YYYY/MM/DD"
                value={endDate}
                onChange={({ detail }) => {
                  setEndDate(detail.value);
                  if (!detail.value) {
                    setEndDateError(t('shiftStep.endDateRequired'));
                  } else if (startDate && detail.value <= startDate) {
                    setEndDateError(t('shiftStep.endDateMustBeAfterStartDate'));
                  } else {
                    setEndDateError('');
                  }
                }}
                onBlur={() => {
                  if (endDate && (!startDate || endDate > startDate)) {
                    updateParent({ endDateTime: new Date(`${endDate}T23:59`) });
                  }
                }}
                i18nStrings={{
                  previousMonthAriaLabel: t('shiftStep.previousMonthAriaLabel'),
                  nextMonthAriaLabel: t('shiftStep.nextMonthAriaLabel'),
                  todayAriaLabel: t('shiftStep.todayAriaLabel'),
                  openCalendarAriaLabel: (selectedDate) =>
                    selectedDate
                      ? t('shiftStep.chooseEndDateAriaLabel', { selectedDate })
                      : t('shiftStep.chooseEndDateDefaultAriaLabel'),
                }}
              />
            </FormField>
          </SpaceBetween>
        )}

        <hr data-volunteer-center-hr />

        <FormField
          description={t('shiftStep.maxVolunteersDescription')}
          label={<OptionalLabel>{t('shiftStep.maxVolunteers')}</OptionalLabel>}
        >
          <Input
            placeholder="0"
            inputMode="numeric"
            value={maxVolunteers}
            onChange={({ detail }) => {
              if (detail.value === '') {
                setMaxVolunteers('');
                return;
              }
              if (!/^\d+$/.test(detail.value)) return;
              const parsed = parseInt(detail.value);
              if (parsed > MAX_VOLUNTEERS_LIMIT) return;
              setMaxVolunteers(String(parsed));
            }}
            onBlur={() =>
              updateParent({ maxSignup: maxVolunteers === '' ? 0 : parseInt(maxVolunteers) })
            }
          />
        </FormField>

        <hr data-volunteer-center-hr />
      </SpaceBetween>
    </ExpandableSection>
  );
}
